import { useMemo } from "react";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ContactForm from "./components/ContactForm";

const values = [
  {
    icon: "ri-flashlight-line",
    title: "快人一步",
    desc: "最新话与连载进度第一时间更新，追更动态绝不滞后。",
  },
  {
    icon: "ri-shield-check-line",
    title: "信息可靠",
    desc: "所有情报均源自官方公开信息，标注清晰，来源可靠。",
  },
  {
    icon: "ri-heart-3-line",
    title: "热爱驱动",
    desc: "由真正的日漫爱好者打造，只为陪你不错过任何一话。",
  },
  {
    icon: "ri-git-branch-line",
    title: "持续进化",
    desc: "内容与功能持续完善，追更体验不断升级。",
  },
];

const stats = [
  { value: "1200+", label: "累计更新篇目" },
  { value: "80+", label: "持续跟踪作品" },
  { value: "5 万+", label: "活跃漫友" },
  { value: "7×24", label: "全天候跟进" },
];

export default function About() {
  const jsonLd = useMemo(() => {
    return [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "日漫情报站",
        url: "https://animesync.github.io/about",
        description: "日漫最新话、日漫连载、日漫更新、日漫情报、日漫剧透、日漫休刊提醒资讯站",
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer service",
          email: "contact@manga-hq.example",
          availableLanguage: "zh-CN",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://animesync.github.io/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://animesync.github.io/about" },
        ],
      },
    ];
  }, []);

  return (
    <>
      <Seo
        title="关于我们 - 品牌介绍·口碑·联系方式 | 日漫情报站"
        description="了解日漫情报站的品牌故事、口碑与联系方式。我们汇总日漫最新话、日漫连载、日漫更新、日漫情报、日漫剧透、日漫休刊提醒，助你不错过任何一话。"
        keywords="日漫,关于我们,日漫情报站,日漫更新,日漫连载,联系方式,订阅"
        path="/about"
        jsonLd={jsonLd}
      />

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />

        {/* 品牌介绍 */}
        <section className="mt-8">
          <div className="relative overflow-hidden rounded-lg bg-foreground-950 px-6 py-12 text-center md:px-12 md:py-16">
            <div className="absolute inset-0 opacity-20">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20anime%20storytelling%20collage%20with%20ink%20brush%20strokes%20and%20books%2C%20warm%20orange%20and%20dark%20tones%2C%20dynamic%20and%20inspiring%2C%20stylized%20editorial%20artwork%2C%20moody%20composition&width=1600&height=700&seq=about-hero&orientation=landscape"
                alt=""
                className="h-full w-full object-cover object-center"
              />
            </div>
            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-white backdrop-blur-sm">
                <i className="ri-heart-3-fill text-accent-400" /> 为热爱而生
              </span>
              <h1 className="mt-5 font-heading text-3xl text-white md:text-5xl">
                关于<span className="text-accent-400">日漫情报站</span>
              </h1>
              <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-white/80">
                我们是深耕日漫追更领域的内容团队，致力于汇总日漫最新话、连载进度与更新动态，并提供日漫情报、日漫剧透与日漫休刊提醒，让每一位漫友都能第一时间掌握追更动态，不错过任何一话。
              </p>
            </div>
          </div>
        </section>

        {/* 数据与口碑 */}
        <section className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4" aria-label="数据概览">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
              <p className="font-heading text-3xl text-primary-600">{stat.value}</p>
              <p className="mt-2 text-sm text-foreground-600">{stat.label}</p>
            </div>
          ))}
        </section>

        {/* 品牌价值 */}
        <section className="mt-12" aria-labelledby="values-heading">
          <div className="text-center">
            <h2 id="values-heading" className="font-heading text-2xl text-foreground-950 md:text-3xl">
              我们的坚持
            </h2>
            <p className="mt-2 text-sm text-foreground-600">四个关键词，诠释我们打造优质追更体验的理念。</p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 联系与订阅 */}
        <section id="contact" className="mt-12 scroll-mt-20" aria-labelledby="contact-heading">
          <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-100 p-6 lg:grid-cols-2 md:p-10">
            <div>
              <h2 id="contact-heading" className="font-heading text-2xl text-foreground-950 md:text-3xl">
                联系我们 · 订阅资讯
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                订阅我们的资讯快报，第一时间获取日漫最新话、连载更新、情报剧透与休刊提醒。如有商务合作或内容建议，也欢迎随时与我们取得联系。
              </p>
              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">邮箱</p>
                    <p className="text-sm text-foreground-600">contact@manga-hq.example</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900">
                    <i className="ri-wechat-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">微信 / 公众号</p>
                    <p className="text-sm text-foreground-600">日漫情报站</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                    <i className="ri-time-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">回复时效</p>
                    <p className="text-sm text-foreground-600">工作日 9:00 - 21:00，通常 24 小时内回复</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h3 className="text-base font-semibold text-foreground-950">填写表单</h3>
              <p className="mt-1 mb-5 text-sm text-foreground-600">留下你的联系方式，我们尽快与你联系。</p>
              <ContactForm />
            </div>
          </div>
        </section>
      </div>
    </>
  );
}